import { ErrorLabel, Input, Popover, PopoverContent, PopoverTrigger } from '@ibsys/kit';
import { XIcon } from 'lucide-react';
import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useUpdateExpertReportBlockManualRating } from '@/entities/expert-report';
import { cn } from '@/shared/lib';
import { IconButton, TEXT_DIFF_ADDED_CLASS, TEXT_DIFF_REMOVED_CLASS, Tooltip } from '@/shared/ui';

type Props = {
  conversationId: number;
  expertReportId: number;
  blockId: number;
  /** Балл, рассчитанный системой */
  rating: number;
  maxRating: number;
  /** Балл, выставленный пользователем; null — балл не выставлялся */
  manualRating: number | null;
  /** Размерность балла; null — размерность не определена */
  unit?: string | null;
};

/**
 * Правка балла блока по месту: поле открывается в поповере и сохраняется по Enter или при уходе фокуса.
 * Балл, равный рассчитанному системой, снимает ручной балл; крестик закрывает окно без сохранения.
 * Правленый балл показан сравнением: балл системы перечёркнут, действующий подсвечен;
 * автор и время правки в заключении не показываются — они видны в разделе «Обратная связь».
 */
export const EvaluationBlockRatingEditor: FC<Props> = ({
  conversationId,
  expertReportId,
  blockId,
  rating,
  maxRating,
  manualRating,
  unit,
}) => {
  const { t } = useTranslation(['dialogs']);
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState(String(manualRating ?? rating));
  const isCancelledRef = useRef(false);
  const { mutateAsync, isPending } = useUpdateExpertReportBlockManualRating();

  // Пока поповер закрыт, поле следует за пришедшим с сервера значением
  useEffect(() => {
    if (!isOpen) setInputValue(String(manualRating ?? rating));
  }, [isOpen, manualRating, rating]);

  const parsed = inputValue.trim() === '' ? null : Number(inputValue);
  const isOutOfRange = parsed !== null && !Number.isNaN(parsed) && (parsed < 0 || parsed > maxRating);

  const saveValue = useCallback(async () => {
    const trimmed = inputValue.trim();
    // Пустое поле ничего не меняет: сохранять нечего
    if (trimmed === '') return;
    const value = Number(trimmed);
    if (Number.isNaN(value) || value < 0 || value > maxRating) return;
    // Балл, совпавший с рассчитанным системой, ручным не считается
    const nextManual = value === rating ? null : value;
    if (nextManual === manualRating) return;
    try {
      await mutateAsync({ conversationId, expertReportId, blockId, manualRating: nextManual });
    } catch {
      // Ошибку показал глобальный перехватчик запросов; поле возвращаем к сохранённому значению
      setInputValue(String(manualRating ?? rating));
    }
  }, [conversationId, expertReportId, blockId, manualRating, rating, maxRating, inputValue, mutateAsync]);

  const handleOpenChange = useCallback(
    (open: boolean) => {
      // Каждое открытие начинается с чистой пометки: иначе отмена прошлой правки погасила бы
      // сохранение следующей
      if (open) isCancelledRef.current = false;
      if (!open) {
        if (isCancelledRef.current) {
          isCancelledRef.current = false;
          setInputValue(String(manualRating ?? rating));
        } else {
          void saveValue();
        }
      }
      setIsOpen(open);
    },
    [saveValue, manualRating, rating],
  );

  const handleCancel = useCallback(() => {
    isCancelledRef.current = true;
    // Контролируемый Popover не зовёт onOpenChange при закрытии снаружи — закрываем общим путём:
    // иначе набранное останется в поле и всплывёт при следующем открытии
    handleOpenChange(false);
  }, [handleOpenChange]);

  /**
   * Escape закрывает окно отменой.
   *
   * Клавишу перехватывает сам Popover, и делает это раньше поля: без этой пометки закрытие пошло бы
   * обычным путём — с сохранением, то есть Escape сохранял бы балл вместо того, чтобы отменить правку.
   */
  const handleEscapeKeyDown = useCallback(() => {
    isCancelledRef.current = true;
  }, []);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        // Балл вне границ блока не сохраняется — окно остаётся открытым с отказом
        if (isOutOfRange) return;
        // Контролируемый Popover не зовёт onOpenChange при закрытии снаружи — сохраняем явно
        handleOpenChange(false);
      }
      // Escape здесь не обрабатываем: клавишу перехватывает сам Popover, он же закрывает окно —
      // отмену помечает `handleEscapeKeyDown`
    },
    [isOutOfRange, handleOpenChange],
  );

  const isManual = manualRating !== null;

  return (
    <Popover open={isOpen} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <span
          role="button"
          tabIndex={-1}
          aria-label={t('dialogs:expertReport.editRating')}
          className={cn(
            'shrink-0 whitespace-nowrap cursor-pointer hover:text-primary/80',
            isManual ? 'text-foreground' : 'text-muted-foreground',
          )}
        >
          {/* Подчёркивание — на самих значениях: заданное на всей строке, оно прошло бы и по баллу
              системы, а снятое у него — погасило бы вместе с собой и перечёркивание.
              Название действия стоит на самих значениях, а не на строке целиком: у балла,
              выставленного вручную, своя подсказка, и подсказка строки спорила бы с ней */}
          {isManual ? (
            <>
              <Tooltip tooltip={t('dialogs:expertReport.editRating')}>
                <s className={cn(TEXT_DIFF_REMOVED_CLASS, 'ml-0 mr-1')}>{rating}</s>
              </Tooltip>
              {/* Подсказка держится на самом балле, а фокус закрывшееся окно возвращает кнопке
                  строки — сама собой подсказка не всплывает, гасить её не от чего */}
              <Tooltip tooltip={t('dialogs:expertReport.manualRatingHint')}>
                <mark className={cn(TEXT_DIFF_ADDED_CLASS, 'mx-0 underline')}>{manualRating}</mark>
              </Tooltip>
            </>
          ) : (
            <Tooltip tooltip={t('dialogs:expertReport.editRating')}>
              <span className="underline">{rating}</span>
            </Tooltip>
          )}{' '}
          {/* Размерность стоит за пределами подчёркивания: правится балл, а не единица, в которой он читается */}
          <Tooltip tooltip={t('dialogs:expertReport.editRating')}>
            <span className="underline">/ {maxRating}</span>
          </Tooltip>
          {unit ? ` ${unit}` : ''}
        </span>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-3" align="start" onEscapeKeyDown={handleEscapeKeyDown}>
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-2">
            <Input
              type="number"
              min={0}
              max={maxRating}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              aria-invalid={isOutOfRange}
              disabled={isPending}
              autoFocus
              className="w-20"
            />
            <span className="text-muted-foreground">
              / {maxRating}
              {unit ? ` ${unit}` : ''}
            </span>
            <IconButton
              variant="ghost"
              size="icon"
              className="size-6 ml-auto"
              label={t('dialogs:expertReport.close')}
              icon={<XIcon className="size-3" />}
              onClick={handleCancel}
            />
          </div>
          {/* Место под подсказку занято всегда и на всю ширину окна: иначе её появление меняет
              высоту окна, и оно перескакивает в другое место экрана */}
          <ErrorLabel className="block min-h-5 text-xs">
            {isOutOfRange ? t('dialogs:expertReport.ratingOutOfRange', { max: maxRating }) : null}
          </ErrorLabel>
        </div>
      </PopoverContent>
    </Popover>
  );
};
